import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, Heart, BookOpen, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const heroImages = [
    { url: '/images/gallery1.jpeg', caption: 'Empowering Students' },
    { url: '/images/gallery2.jpeg', caption: 'Career Counseling Sessions' },
    { url: '/images/gallery3.jpeg', caption: 'Women Empowerment Programs' },
    { url: '/images/gallery4.jpeg', caption: 'Counseling and Guidance' },
    { url: '/images/gallery5.jpeg', caption: 'Community Awareness' },
    { url: '/images/gallery6.jpeg', caption: 'Student Workshops' },
  ];

  // Track visitor on page load
  useEffect(() => {
    const trackVisit = async () => {
      // Check if already tracked in this session
      const hasTracked = sessionStorage.getItem('visitor_tracked');
      
      if (!hasTracked) {
        try {
          await axios.post(`${API}/visitor`, {
            page: window.location.pathname,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
          });
          sessionStorage.setItem('visitor_tracked', 'true');
        } catch (error) {
          // Silently fail - visitor tracking is non-critical
        }
      }
    };
    
    trackVisit();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Carousel Section */}
      <section className="carousel-container relative">
        {heroImages.map((image, index) => (
          <div
            key={`hero-${index}-${image.url}`}
            className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
          >
            <img src={image.url} alt={image.caption} className="carousel-image" />
            <div className="carousel-overlay">
              <div className="text-center text-white px-4">
                <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
                  RP Educational Welfare Trust
                </h1>
                <p className="text-xl md:text-2xl mb-4 hindi-text">
                  शिक्षा से सशक्तिकरण, ज्ञान से परिवर्तन
                </p>
                <p className="text-lg md:text-xl mb-8">
                  Empowering Through Education, Transforming Through Knowledge
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/donate"
                    className="bg-white text-purple-700 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center"
                  >
                    Donate Now <Heart className="ml-2" size={20} />
                  </Link>
                  <Link
                    to="/about"
                    className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-purple-700 transition-all duration-300 flex items-center justify-center"
                  >
                    Learn More <ArrowRight className="ml-2" size={20} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        <button onClick={prevSlide} className="carousel-nav prev">
          <ChevronLeft size={30} />
        </button>
        <button onClick={nextSlide} className="carousel-nav next">
          <ChevronRight size={30} />
        </button>
        
        <div className="carousel-dots">
          {heroImages.map((_, index) => (
            <div
              key={`dot-${index}`}
              onClick={() => setCurrentSlide(index)}
              className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '5000+', label: 'Students Counseled', hindi: 'छात्रों को परामर्श दिया' },
              { number: '200+', label: 'Workshops Conducted', hindi: 'कार्यशालाएं आयोजित' },
              { number: '100+', label: 'Women Empowered', hindi: 'महिलाओं को सशक्त बनाया' },
              { number: '600+', label: 'Families Benefited', hindi: 'परिवारों को लाभ' },
            ].map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-4xl font-bold text-purple-700 mb-2">{stat.number}</h3>
                <p className="text-gray-600">{stat.label}</p>
                <p className="text-sm text-gray-500 hindi-text">{stat.hindi}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
{/* Mission Section */}
      <section className="section-padding bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-xl text-gray-600 hindi-text">हमारा मिशन</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <GraduationCap size={48} />,
                title: 'Quality Education',
                hindi: 'गुणवत्तापूर्ण शिक्षा',
                description: 'Providing access to quality education for rural students',
              },
              {
                icon: <Users size={48} />,
                title: 'Career Counseling',
                hindi: 'करियर परामर्श',
                description: 'Guiding students towards successful career paths',
              },
              {
                icon: <Heart size={48} />,
                title: 'Women Empowerment',
                hindi: 'महिला सशक्तिकरण',
                description: 'Empowering women through education and skills',
              },
              {
                icon: <BookOpen size={48} />,
                title: 'Skill Development',
                hindi: 'कौशल विकास',
                description: 'Building practical skills for better employment',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 text-center"
              >
                <div className="text-purple-700 mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 hindi-text mb-3">{item.hindi}</p>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-purple-700 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Help Us Transform Lives Through Education
          </h2>
          <p className="text-xl mb-8 hindi-text">
            शिक्षा के माध्यम से जीवन बदलने में हमारी मदद करें
          </p>
          <p className="text-lg mb-8 max-w-3xl mx-auto">
            Your contribution can change someone's life forever
          </p>
          <Link
            to="/donate"
            className="bg-white text-purple-700 px-10 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 inline-flex items-center"
          >
            Donate Now <Heart className="ml-2" size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
